import React from 'react';
import {
  makeStyles,
  tokens,
  Card,
  Text,
  Button,
  ProgressBar,
} from '@fluentui/react-components';
import {
  Dismiss24Regular,
  CheckmarkCircle24Filled,
  ErrorCircle24Filled,
  ArrowDownload24Regular,
} from '@fluentui/react-icons';
import { useExport } from '../../contexts/ExportContext';

const useStyles = makeStyles({
  container: {
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    zIndex: 10000,
    minWidth: '320px',
    maxWidth: '400px',
  },
  card: {
    padding: '16px',
    boxShadow: tokens.shadow16,
    borderRadius: tokens.borderRadiusMedium,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '8px',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  title: {
    fontWeight: tokens.fontWeightSemibold,
  },
  closeButton: {
    minWidth: 'auto',
    padding: '4px',
  },
  progressSection: {
    marginTop: '8px',
  },
  progressBar: {
    marginTop: '8px',
    marginBottom: '4px',
  },
  progressDetails: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '4px',
  },
  successIcon: {
    color: tokens.colorPaletteGreenForeground1,
  },
  errorIcon: {
    color: tokens.colorPaletteRedForeground1,
  },
  errorMessage: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
  },
  successMessage: {
    color: tokens.colorPaletteGreenForeground1,
    fontSize: tokens.fontSizeBase200,
  },
  filename: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    marginTop: '4px',
    wordBreak: 'break-all',
  },
  infoText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  phaseText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  percentText: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorBrandForeground1,
  },
});

// Traducao das fases
function getPhaseLabel(phase: string): string {
  switch (phase) {
    case 'starting':
      return 'Iniciando...';
    case 'fetching':
      return 'Buscando operacoes...';
    case 'loading-details':
      return 'Carregando detalhes...';
    case 'generating':
      return 'Gerando CSV...';
    case 'done':
      return 'Concluido!';
    default:
      return 'Processando...';
  }
}

export const ExportToast: React.FC = () => {
  const styles = useStyles();
  const { exportState, dismissExport } = useExport();

  // Nao mostra se nao ha nada para mostrar
  if (!exportState.isExporting && !exportState.success && !exportState.error) {
    return null;
  }

  return (
    <div className={styles.container}>
      <Card className={styles.card}>
        <div className={styles.header}>
          <div className={styles.titleRow}>
            {exportState.isExporting && (
              <>
                <ArrowDownload24Regular />
                <Text className={styles.title}>Exportando CSV</Text>
              </>
            )}
            {exportState.success && (
              <>
                <CheckmarkCircle24Filled className={styles.successIcon} />
                <Text className={styles.title}>Export concluido</Text>
              </>
            )}
            {exportState.error && (
              <>
                <ErrorCircle24Filled className={styles.errorIcon} />
                <Text className={styles.title}>Erro no export</Text>
              </>
            )}
          </div>
          <Button
            className={styles.closeButton}
            appearance="subtle"
            icon={<Dismiss24Regular />}
            onClick={dismissExport}
            title="Fechar"
          />
        </div>

        {exportState.isExporting && (
          <div className={styles.progressSection}>
            <Text className={styles.phaseText}>
              {getPhaseLabel(exportState.phase)}
            </Text>
            <ProgressBar
              className={styles.progressBar}
              value={exportState.progress / 100}
              max={1}
            />
            <div className={styles.progressDetails}>
              <Text className={styles.infoText}>
                {exportState.totalOperations > 0
                  ? `${exportState.processedOperations.toLocaleString('pt-BR')} / ${exportState.totalOperations.toLocaleString('pt-BR')} operacoes`
                  : 'Calculando...'}
              </Text>
              <Text className={styles.percentText}>
                {exportState.progress}%
              </Text>
            </div>
          </div>
        )}

        {exportState.success && (
          <>
            <Text className={styles.successMessage}>
              Arquivo baixado com sucesso!
            </Text>
            {exportState.filename && (
              <Text className={styles.filename}>{exportState.filename}</Text>
            )}
            {exportState.totalOperations > 0 && (
              <Text className={styles.infoText}>
                {exportState.totalOperations.toLocaleString('pt-BR')} operacoes exportadas
              </Text>
            )}
          </>
        )}

        {exportState.error && (
          <Text className={styles.errorMessage}>{exportState.error}</Text>
        )}
      </Card>
    </div>
  );
};

export default ExportToast;
